import { makeAutoObservable, runInAction } from "mobx";
import OnboardingDataStore, {
    IGetMerchantOnboardingModel,
    IOnboardingScreenStore
} from "./onboarding-data-store";
import { ONBOARDING_STEPS } from "../constants";
import { OnboardingNavigationStore } from "./onboarding-navigation-store";
import walletStore, { WalletStore } from "../../billing/stores/wallet/wallet-store";
import { IMerchantOnboarding } from "../onboarding-types";
import { WALLET_SCREEN_TYPE } from "../components/wallet-configuration-onboarding-screen/wallet-configuration-onboarding-screen";


class WalletConfigurationStore implements IOnboardingScreenStore {
    private readonly _stepName: ONBOARDING_STEPS = ONBOARDING_STEPS.WALLET_CONFIGURATION;
    private _getModel: IGetMerchantOnboardingModel;
    private _isLoading: boolean = false;
    private _hasError: boolean | null = null;
    private _paymentMethodVerified: boolean | null = null;
    private _autoNextStepTimeout: NodeJS.Timeout | null = null;

    constructor(private _onboardingDataStore: OnboardingDataStore, private _navigationStore: OnboardingNavigationStore) {
        this._getModel = this._onboardingDataStore.getModel;
        makeAutoObservable(this, {}, { autoBind: true });
    }

    public get walletStore(): WalletStore {
        return walletStore;
    }

    public get isBusy(): boolean { 
        return this._isLoading 
            || this.walletStore.isLoading 
            || this.walletStore.isUpdating 
            || this.walletStore.isAddingPaymentMethod
            || this.walletStore.isDeleting; 
    }

    public get isLoading(): boolean { 
        return this._isLoading || this.walletStore.isLoading;
    }

    public hasChanges(): boolean {
        return this._getModel()?.wallet_configured !== this.walletStore.hasPaymentMethod;
    }

    public get currentWalletScreen(): WALLET_SCREEN_TYPE {
        /** if we get error on any step - show error screen */
        if (this.paymentMethodHasErrors) {
            return WALLET_SCREEN_TYPE.PAYMENT_METHOD_ERROR;
        }

        /** if user has payment method - show configuration screen */
        if (this.walletStore.hasPaymentMethod) {
            return WALLET_SCREEN_TYPE.PAYMENT_METHOD_CONFIGURATION;
        }

        return WALLET_SCREEN_TYPE.ADDING_PAYMENT_METHOD;
    }

    public get paymentMethodHasErrors(): boolean | null {
        return this._hasError;
    }

    public get paymentMethodVerified(): boolean | null {
        return this._paymentMethodVerified;
    }
    
    public validationErrors(): string[] {
        const errors: string[] = [];

        /* If the user does not have a payment method, we do not need to validate the amounts */
        if (!this.walletStore.hasPaymentMethod || this.walletStore.isUpdating) {
            return errors;
        }

        if (!this.walletStore.validateRechargeAmountEditor().isValid) {
            errors.push(...this.walletStore.validateRechargeAmountEditor().messages);
        }
        
        if (!this.walletStore.validateMinimumThresholdEditor().isValid) {
            errors.push(...this.walletStore.validateMinimumThresholdEditor().messages);
        }
        
        return errors;
    };

    public discardChanges(): void {
        console.log('[WalletConfigurationStore] discardChanges not implemented');
    }

    public getUpdatedModel(): IMerchantOnboarding {
        /** wallet can be treated as configured if payment method was and recharge amount and threshold were set */
        const walletConfigured = this.walletStore.hasPaymentMethod 
            && this.walletStore.rechargeAmount !== null 
            && this.walletStore.rechargeThreshold !== null;
        
        return { 
            ...this._getModel(), 
            wallet_configured: walletConfigured
        } as IMerchantOnboarding;
    }

    public getMixPanelData(): Record<string, any> {
        return {};
    }

    public isComplete(): boolean {
        return this._getModel()?.wallet_configured !== null;
    }

    public previousStep(): void {
        this._navigationStore.previousStep();
    }

    public async nextStep(): Promise<void> {
        this._isLoading = true;
        await this._onboardingDataStore.save(this._stepName);
        runInAction(() => {
            this._navigationStore.nextStep();
            this._isLoading = false;
        });
    }

    public backToAddingPaymentMethod(): void {
        this._hasError = null;
    }

    public async addPaymentMethodHandler(): Promise<void> {
        await this.walletStore.addPaymentMethodHandler();
        /** If we still do not have payment method - show error */
        if (!this.walletStore.hasPaymentMethod) {
            this._hasError = true;
        }
    }

    public async updateWalletAndNextStep(): Promise<void> {
        /** 
         * If we have timeout - it means we already clicked the button once - so we need to skip the timeout and go to next step.
         * */
        if (this._autoNextStepTimeout !== null) {
            clearTimeout(this._autoNextStepTimeout);
            this._autoNextStepTimeout = null;
            this.nextStep();
            return;
        }

        this._isLoading = true;
        
        try {
            await this.walletStore.updateWalletWithAutoDeposit();
        
            runInAction(() => {
                this._paymentMethodVerified = true;
                this._isLoading = false;
            });

            this._autoNextStepTimeout = setTimeout(() => {
                runInAction(() => {
                    this.nextStep();
                    this._paymentMethodVerified = null;
                });
            }, 1500);
        } catch (e) {
            runInAction(() => {
                this._isLoading = false;
                this._hasError = true;
            });
        }    
    }

    public async setupNowClick(): Promise<void> {
        this.walletStore.addPaymentMethodHandler();
    }
}

export default WalletConfigurationStore;
